import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GlobalContext } from '@/app/providers/globalState';
import { setMockTransport } from '@/shared/http/client';
import { DEMO_AVAILABLE_PROVIDERS } from './demoFixtures';
import { createDemoMockTransport, settleDemoProcessing } from './mockTransport';
import { DemoTourScopeContext } from './demoTourScope';
import { WizardApiProvider } from './wizardApi';

const noop = () => {};

/**
 * Keeps the in-memory backend installed for exactly as long as the scope is
 * mounted. The transport is created and installed in the lazy initializer
 * (during the parent's render) because the hosted pages' own mount effects
 * run before this component's effects would, and their first requests must
 * already hit the mock. The effect only re-installs after a StrictMode
 * double-invoke and uninstalls on unmount.
 */
function useDemoMockTransport(usecaseRef, onChangeRef) {
    const [transport] = useState(() => {
        const created = createDemoMockTransport({
            getUsecase: () => usecaseRef.current,
            setUsecase: (patch) =>
                onChangeRef.current?.((current) => ({ ...current, ...patch })),
            onUnhandled: import.meta.env.DEV ? (message) => console.warn(message) : undefined,
        });
        setMockTransport(created);
        return created;
    });
    useEffect(() => {
        const uninstall = setMockTransport(transport);
        return uninstall;
    }, [transport]);
    return transport;
}

/**
 * Re-provides `GlobalContext` around the demo wizard so the hosted pages
 * (`ModelSetup`, `DataSelection`, `TrainFromSpecificUsecase`, the Data
 * Collection panel) render against the in-memory demo task exactly as they
 * would against a real one, and every update they make lands in the demo
 * task's local state instead of the workspace.
 *
 * Everything the pages read about tasks is overridden: `allUsecases` holds
 * only the demo task, the derived `allApps` / `currentSubGroup(Key)` are
 * recomputed with the same grouping the real provider uses, `refreshUsecases`
 * answers synchronously (and still calls its callback — the Data Collection
 * panel moves to the next step from it), and the commercial-model catalog is
 * the demo one so Model Setup offers `gpt-5` without a `GET available-models`.
 * Progress items (label generation armed on Model Setup → Next) stay local
 * too, so nothing of the demo leaks into the real app shell's banners.
 * Everything else (auth, websocket plumbing, settings) passes through.
 *
 * While mounted it also installs the in-memory backend from
 * `mockTransport.js`, so every request the hosted pages make is answered
 * from the IMDb fixtures and the demo never touches the API.
 *
 * Props: `usecase` (the demo task), `onChange(updater)` (replace or patch it,
 * `useState`-style).
 */
export default function DemoScope({ usecase, onChange, children }) {
    const base = useContext(GlobalContext) || {};
    const [progressStatus, setProgressState] = useState({});
    const usecaseRef = useRef(usecase);
    const onChangeRef = useRef(onChange);
    usecaseRef.current = usecase;
    onChangeRef.current = onChange;
    useDemoMockTransport(usecaseRef, onChangeRef);

    // Demo uploads have no data_processor behind them: a card that reaches
    // "processing" completes immediately instead of waiting for WS events.
    const setProgressStatus = useCallback((update) => {
        setProgressState((prev) =>
            settleDemoProcessing(typeof update === 'function' ? update(prev) : update)
        );
    }, []);

    const patchDemoUsecase = useCallback(
        (update) =>
            onChange((current) => ({
                ...current,
                ...(typeof update === 'function' ? update(current) : update),
            })),
        [onChange]
    );

    const updateUsecase = useCallback(
        (usecaseId, update) => {
            if (usecaseId === usecase._id) patchDemoUsecase(update);
        },
        [usecase._id, patchDemoUsecase]
    );

    const updatePromptAndTeacher = useCallback(
        (prompt, teacher_model, comparison_mode, teacher_provider, teacher_model_display) => {
            const update = { prompt, teacher_model };
            if (comparison_mode !== undefined) update.comparison_mode = comparison_mode;
            if (teacher_provider !== undefined) update.teacher_provider = teacher_provider;
            if (teacher_model_display !== undefined) update.teacher_model_display = teacher_model_display;
            patchDemoUsecase(update);
        },
        [patchDemoUsecase]
    );

    const updateParserFunction = useCallback(
        (parser_function) =>
            patchDemoUsecase({
                parser_function,
                parser_function_validation_failed: false,
                parser_function_validation_message: null,
            }),
        [patchDemoUsecase]
    );

    const updateParserValidationStatus = useCallback(
        (failed, message = null) =>
            patchDemoUsecase({
                parser_function_validation_failed: failed,
                parser_function_validation_message: message,
            }),
        [patchDemoUsecase]
    );

    const updateStatus = useCallback(
        (usecaseId, status) => updateUsecase(usecaseId, { status }),
        [updateUsecase]
    );

    const updateVersion = useCallback(
        (usecaseId, version) => updateUsecase(usecaseId, { version }),
        [updateUsecase]
    );

    const refreshUsecases = useCallback(
        (successCallback) => {
            successCallback?.([usecase]);
            return Promise.resolve([usecase]);
        },
        [usecase]
    );

    const removeProgressItem = useCallback((datasetName) => {
        setProgressState((prev) => {
            if (!(datasetName in prev)) return prev;
            const next = { ...prev };
            delete next[datasetName];
            return next;
        });
    }, []);

    const value = useMemo(() => {
        const subGroup = { sub_group_name: usecase.sub_group_name?.trim() || usecase.usecase_name, usecases: [usecase] };
        return {
            ...base,
            allUsecases: [usecase],
            allApps: [{ group_name: usecase.group_name, project_goal: usecase.project_goal || '', sub_groups: [subGroup] }],
            isFetchingUsecases: false,
            setAllUsecases: noop,
            currentUsecaseId: usecase._id,
            setCurrentUsecaseId: noop,
            currentUsecase: usecase,
            setCurrentUsecase: noop,
            getCurrentUsecase: () => usecase,
            currentSubGroup: subGroup,
            currentSubGroupKey: { group_name: usecase.group_name, sub_group_name: subGroup.sub_group_name },
            updateUsecase,
            updatePromptAndTeacher,
            updateParserFunction,
            updateParserValidationStatus,
            updateStatus,
            updateVersion,
            refreshUsecases,
            progressStatus,
            setProgressStatus,
            removeProgressItem,
            seedGenerationProgress: noop,
            seedRetrievalAgentProgress: noop,
            registerStartedTraining: noop,
            markTrainingStatusCanceled: noop,
            triggerModelDownloadSave: noop,
            availableProviders: DEMO_AVAILABLE_PROVIDERS,
            isCommercialModelsLoading: false,
            refreshCommercialModels: noop,
        };
    }, [
        base,
        usecase,
        updateUsecase,
        updatePromptAndTeacher,
        updateParserFunction,
        updateParserValidationStatus,
        updateStatus,
        updateVersion,
        refreshUsecases,
        progressStatus,
        setProgressStatus,
        removeProgressItem,
    ]);

    // The pages inside the scope publish their imperative APIs here, for the
    // tour's auto-fill script (`demoScript.js`) to drive. The scope context
    // is what tells them they are in the demo (`useDemoTourActive`).
    return (
        <DemoTourScopeContext.Provider value>
            <GlobalContext.Provider value={value}>
                <WizardApiProvider>{children}</WizardApiProvider>
            </GlobalContext.Provider>
        </DemoTourScopeContext.Provider>
    );
}
